import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { memberService } from '../../services/memberService';
import { reservationService } from '../../services/reservationService';
import { useAuth } from '../../context/AuthContext';
import { Loader } from '../../components/Loader';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Title = styled.h1`
  color: ${colors.primary};
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: ${colors.textLight};
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const Card = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const CardTitle = styled.h3`
  color: ${colors.textLight};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 10px;
`;

const CardValue = styled.div`
  color: ${colors.primary};
  font-size: 36px;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Section = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
  margin-bottom: 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const SectionTitle = styled.h2`
  color: ${colors.primary};
  margin-bottom: 20px;
  font-size: 20px;
`;

const ReservationItem = styled.div`
  padding: 15px;
  background-color: ${colors.background};
  border-radius: 6px;
  margin-bottom: 10px;
  border-left: 4px solid ${colors.primary};
`;

const ReservationTitle = styled.div`
  font-weight: 500;
  color: ${colors.text};
  margin-bottom: 8px;
  font-size: 16px;
`;

const ReservationInfo = styled.div`
  color: ${colors.textLight};
  font-size: 14px;
  margin-bottom: 4px;
`;

const Alert = styled.div`
  background-color: ${props => {
    if (props.type === 'warning') return '#fff3cd';
    if (props.type === 'danger') return '#f8d7da';
    return '#d1ecf1';
  }};
  color: ${props => {
    if (props.type === 'warning') return '#856404';
    if (props.type === 'danger') return '#721c24';
    return '#0c5460';
  }};
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  border-left: 4px solid ${props => {
    if (props.type === 'warning') return '#ffc107';
    if (props.type === 'danger') return '#dc3545';
    return '#17a2b8';
  }};
`;

export const MemberDashboard = () => {
  const { user } = useAuth();
  const [memberData, setMemberData] = useState(null);
  const [upcomingReservations, setUpcomingReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [member, reservations] = await Promise.all([
        memberService.getMember(user.id),
        reservationService.getMemberReservations(user.id)
      ]);

      setMemberData(member);
      
      // Filter upcoming approved reservations
      const today = new Date().toISOString().split('T')[0];
      const upcoming = reservations.filter(r => 
        r.status === 'approved' && 
        r.classDetails?.date >= today
      );
      setUpcomingReservations(upcoming);

    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Title>Hoş Geldiniz, {memberData?.firstName}!</Title>
      <Subtitle>Seed Yoga Studio Üye Paneli</Subtitle>

      {memberData?.remainingDays <= 7 && memberData?.remainingDays > 0 && (
        <Alert type="warning">
          ⚠️ Üyeliğinizin sona ermesine <strong>{memberData.remainingDays} gün</strong> kaldı. 
          Lütfen üyeliğinizi yenileyin.
        </Alert>
      )}

      {memberData?.definitionType === 'reservation' && memberData?.remainingReservations <= 2 && memberData?.remainingReservations > 0 && (
        <Alert type="danger">
          ⚠️ Kalan rezervasyon hakkınız: <strong>{memberData.remainingReservations}</strong>
        </Alert>
      )}

      {memberData?.definitionType === 'reservation' && memberData?.remainingReservations === 0 && (
        <Alert type="danger">
          ❌ Rezervasyon hakkınız dolmuş. Lütfen Seed Yoga Studio ile iletişime geçin.
        </Alert>
      )}

      <Grid>
        <Card>
          <CardTitle>Kalan Üyelik Süresi</CardTitle>
          <CardValue>{memberData?.remainingDays}</CardValue>
          <p style={{ color: colors.textLight, fontSize: '14px', marginTop: '10px' }}>
            Gün
          </p>
        </Card>

        {memberData?.definitionType === 'reservation' && (
          <Card>
            <CardTitle>Kalan Rezervasyon Hakkı</CardTitle>
            <CardValue>{memberData?.remainingReservations}</CardValue>
            <p style={{ color: colors.textLight, fontSize: '14px', marginTop: '10px' }}>
              / {memberData?.reservationLimit} Toplam
            </p>
          </Card>
        )}

        <Card>
          <CardTitle>Yaklaşan Dersler</CardTitle>
          <CardValue>{upcomingReservations.length}</CardValue>
          <p style={{ color: colors.textLight, fontSize: '14px', marginTop: '10px' }}>
            Onaylı Rezervasyon
          </p>
        </Card>

        <Card>
          <CardTitle>Üyelik Durumu</CardTitle>
          <CardValue style={{ fontSize: '24px' }}>
            {memberData?.isActive ? '✓ Aktif' : '✗ Pasif'}
          </CardValue>
        </Card>
      </Grid>

      {upcomingReservations.length > 0 && (
        <Section>
          <SectionTitle>Yaklaşan Dersleriniz</SectionTitle>
          {upcomingReservations.slice(0, 5).map(reservation => (
            <ReservationItem key={reservation.id}>
              <ReservationTitle>
                {reservation.classDetails?.title}
              </ReservationTitle>
              <ReservationInfo>
                👨‍🏫 Eğitmen: {reservation.classDetails?.instructor}
              </ReservationInfo>
              <ReservationInfo>
                📅 {reservation.classDetails?.date} - 🕐 {reservation.classDetails?.time}
              </ReservationInfo>
            </ReservationItem>
          ))}
        </Section>
      )}
    </Container>
  );
};

